import { useState, useMemo } from 'react';
import PageHero from '../components/PageHero.jsx';
import FilterBar from '../components/FilterBar.jsx';
import ClubCard from '../components/ClubCard.jsx';
import { clubs } from '../data/clubs.js';
import { getFavoriteClubs, toggleFavoriteClub } from '../lib/storage.js';
import './Clubs.css';

// ── Filter config ─────────────────────────────────────────────────────────────

const TOTAL = clubs.length;

const GRADE_OPTIONS    = ['All', '6', '7', '8'];
const CATEGORY_OPTIONS = ['All', 'Academic', 'Arts', 'Service', 'STEM', 'Spirit', 'Special Interest'];
const DAY_OPTIONS      = ['All', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Varies', 'TBA'];

const CATEGORY_MAP = {
  'All':              null,
  'Academic':         'academic',
  'Arts':             'arts',
  'Service':          'service',
  'STEM':             'stem',
  'Spirit':           'spirit',
  'Special Interest': 'special-interest',
};

const FILTER_DEFAULTS = { grade: 'All', category: 'All', day: 'All' };

const FEATURED     = clubs.filter(c => c.highlight);
const FEATURED_IDS = new Set(FEATURED.map(c => c.id));

// ── Helpers ───────────────────────────────────────────────────────────────────

function matchesDay(club, day) {
  if (day === 'All') return true;
  if (!club.meetingDay) return false;
  return club.meetingDay.includes(day);
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Clubs() {
  const [grade,         setGrade]         = useState(FILTER_DEFAULTS.grade);
  const [category,      setCategory]      = useState(FILTER_DEFAULTS.category);
  const [day,           setDay]           = useState(FILTER_DEFAULTS.day);
  const [search,        setSearch]        = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteClubs, setFavoriteClubs] = useState(() => getFavoriteClubs());

  // ── Filtering ──────────────────────────────────────────────────────────────

  const hasActiveFilters =
    grade    !== FILTER_DEFAULTS.grade    ||
    category !== FILTER_DEFAULTS.category ||
    day      !== FILTER_DEFAULTS.day      ||
    search.trim() !== ''                  ||
    favoritesOnly;

  const filtered = useMemo(() => {
    const q   = search.trim().toLowerCase();
    const cat = CATEGORY_MAP[category];
    return clubs.filter(c => {
      if (grade !== 'All' && !c.grades.includes(Number(grade))) return false;
      if (cat && c.category !== cat) return false;
      if (!matchesDay(c, day)) return false;
      if (favoritesOnly && !favoriteClubs.includes(c.id)) return false;
      if (q) {
        const inName  = c.name.toLowerCase().includes(q);
        const inShort = c.shortName && c.shortName.toLowerCase().includes(q);
        const inDesc  = c.description && c.description.toLowerCase().includes(q);
        if (!inName && !inShort && !inDesc) return false;
      }
      return true;
    });
  }, [grade, category, day, search, favoritesOnly, favoriteClubs]);

  // When no filters active, split featured from the rest
  const showFeatured  = !hasActiveFilters;
  const mainClubs     = showFeatured
    ? filtered.filter(c => !FEATURED_IDS.has(c.id))
    : filtered;

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleFilterChange(key, value) {
    if (key === 'grade')    setGrade(value);
    if (key === 'category') setCategory(value);
    if (key === 'day')      setDay(value);
  }

  function handleClear() {
    setGrade(FILTER_DEFAULTS.grade);
    setCategory(FILTER_DEFAULTS.category);
    setDay(FILTER_DEFAULTS.day);
    setSearch('');
    setFavoritesOnly(false);
  }

  function handleToggleFavorite(id) {
    const next = toggleFavoriteClub(id);
    setFavoriteClubs(next);
  }

  const filters = [
    { key: 'grade',    label: 'Grade',    options: GRADE_OPTIONS,    value: grade    },
    { key: 'category', label: 'Category', options: CATEGORY_OPTIONS, value: category },
    { key: 'day',      label: 'Day',      options: DAY_OPTIONS,      value: day      },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="clubs">
      <PageHero
        icon="/assets/eagle/page-icons/clubs.png"
        title="Clubs at Canyon Ridge"
      />
      <p className="clubs-subtitle">
        All {TOTAL} clubs at CRMS. Tap the heart to favorite the ones you're
        interested in — your list saves automatically.
      </p>

      <FilterBar
        filters={filters}
        search={{ value: search, placeholder: 'Search clubs…' }}
        onChange={handleFilterChange}
        onSearchChange={setSearch}
        onClear={handleClear}
        hasActiveFilters={hasActiveFilters}
      />

      {/* ── Favorites-only toggle (outside FilterBar) ── */}
      <div className="clubs-extras-row">
        <button
          type="button"
          className={`clubs-fav-toggle${favoritesOnly ? ' clubs-fav-toggle--active' : ''}`}
          aria-pressed={favoritesOnly}
          onClick={() => setFavoritesOnly(f => !f)}
        >
          <span aria-hidden="true">{favoritesOnly ? '♥' : '♡'}</span>
          Favorites only
        </button>
      </div>

      {/* ── Result count ── */}
      <p className="clubs-count">
        Showing <strong>{filtered.length}</strong> of {TOTAL} clubs
      </p>

      {/* ── Featured section (default view only) ── */}
      {showFeatured && FEATURED.length > 0 && (
        <section className="clubs-featured">
          <h2 className="clubs-featured-heading">Featured</h2>
          <div className="clubs-grid">
            {FEATURED.map(club => (
              <ClubCard
                key={club.id}
                club={club}
                isFavorited={favoriteClubs.includes(club.id)}
                onToggleFavorite={handleToggleFavorite}
                showFeaturedPill
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Empty state ── */}
      {filtered.length === 0 && (
        <div className="clubs-empty">
          {favoritesOnly && favoriteClubs.length === 0 ? (
            <>
              <p>No favorites yet.</p>
              <p className="clubs-empty-hint">
                Tap the <span aria-hidden="true">♡</span> on any club card to save it here.
              </p>
            </>
          ) : (
            <>
              <p>No clubs match those filters. Try clearing some.</p>
              <button type="button" className="clubs-empty-clear" onClick={handleClear}>
                Clear filters
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Main grid ── */}
      {mainClubs.length > 0 && (
        <div className={`clubs-grid${showFeatured ? ' clubs-grid--after-featured' : ''}`}>
          {mainClubs.map(club => (
            <ClubCard
              key={club.id}
              club={club}
              isFavorited={favoriteClubs.includes(club.id)}
              onToggleFavorite={handleToggleFavorite}
              showFeaturedPill={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
